/**
 * The data the expedition map renders. Presentation-free: an adapter builds
 * it from the course roadmap (see `ExpeditionRoadmapView`), and the
 * prototype gallery builds it from fixture data.
 */

/** What an item is, which picks its icon and its click behaviour. */
export type ExpeditionItemKind =
  | 'learn'
  | 'code'
  | 'math'
  | 'section'
  | 'link'
  | 'check';

/** One learning experience on the map. */
export interface ExpeditionItem {
  /** Stable id, unique across all weeks and both tracks. */
  id: string;
  kind: ExpeditionItemKind;
  title: string;
  /**
   * One-line hook shown as a tooltip where a layout has room for it, and in
   * a link item's callout.
   */
  blurb?: string;
  /**
   * Where a `link` item goes: an in-app path or an external URL. A link item
   * with one opens a callout on its first press and follows it on the
   * second. When omitted, a press calls the map's `onSelect` directly.
   */
  href?: string;
  /**
   * Id of the item this one builds on, for layouts that draw prerequisites.
   * When omitted, the item hangs off its week rather than off a specific item.
   */
  unlockedBy?: string;
  /**
   * Whether the student can still finish the item. When false (a link that
   * records no completion, a section whose meeting has ended), the item is
   * never the next item. Defaults to true.
   */
  isFinishable?: boolean;
}

/** One week of the course, rendered as one land. */
export interface ExpeditionWeek {
  id: string;
  /** Short heading, e.g. "Week 1" or "Application". */
  label: string;
  /** The week's theme, shown as the land's title. */
  title: string;
  /** Display date of the week's start, when known. */
  date?: string;
  /** Who leads the week, when known. */
  instructor?: string;
  /** Whether the week is not yet available. Locked lands fade and disable. */
  isLocked: boolean;
  /** The main line every student is expected to follow. */
  main: ExpeditionItem[];
  /** Optional experiences that run alongside the main line. */
  goingFurther: ExpeditionItem[];
}

/**
 * The student's next item: the first main-line item, in course order across
 * the unlocked weeks, that is unfinished and can still be finished. Both maps
 * put their START marker on it, and the student home opens on it.
 *
 * @param args.weeks - Weeks in course order.
 * @param args.completed - Ids of completed items.
 * @return The item, or undefined when nothing finishable is left.
 * @throws Never.
 */
export function findNextItem({
  weeks,
  completed,
}: {
  weeks: readonly ExpeditionWeek[];
  completed: ReadonlySet<string>;
}): ExpeditionItem | undefined {
  return weeks
    .filter(week => !week.isLocked)
    .flatMap(week => week.main)
    .find(item => item.isFinishable !== false && !completed.has(item.id));
}

/**
 * The tooltip for an item's button. An item in a locked week that the viewer
 * may still open says so first, since it keeps the locked look.
 *
 * @param args.item - The item.
 * @param args.isLocked - Whether the item's week is locked.
 * @param args.canOpenLocked - Whether the viewer may open locked items.
 * @return The tooltip, or undefined when there is nothing to show.
 * @throws Never.
 */
export function itemTooltip({
  item,
  isLocked,
  canOpenLocked,
}: {
  item: ExpeditionItem;
  isLocked: boolean;
  canOpenLocked: boolean;
}): string | undefined {
  if (!isLocked || !canOpenLocked) {
    return item.blurb;
  }
  const note = 'Locked for students. You can open it as an admin.';
  return item.blurb === undefined ? note : `${note}\n${item.blurb}`;
}
